import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderCalendarChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);

    // Map data to [date, value]
    const dateField = config.dateField || "date";
    const valueField = config.valueField || "value";

    const chartData = data.map(row => {
        const dateRaw = row[dateField];
        let dateStr = "";

        if (dateRaw instanceof Date) {
            dateStr = echarts.time.format(dateRaw, '{yyyy}-{MM}-{dd}', false);
        } else {
            dateStr = String(dateRaw || "");
        }

        return [dateStr, Number(row[valueField]) || 0];
    });

    // Determine range (year)
    let range: string | number | string[] = config.year || "2024";
    if (!config.year && data.length > 0) {
        // Try to extract year from first data point
        const firstDate = data[0][dateField];
        if (firstDate) {
            const date = new Date(firstDate as string);
            if (!isNaN(date.getFullYear())) {
                range = String(date.getFullYear());
            }
        }
    }

    // Determine min/max for visualMap
    const values = chartData.map(d => d[1] as number);
    const minVal = config.minValue !== undefined ? Number(config.minValue) : (values.length > 0 ? Math.min(...values, 0) : 0);
    const maxVal = config.maxValue !== undefined ? Number(config.maxValue) : (values.length > 0 ? Math.max(...values) : 100);

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
            position: 'top',
            formatter: (p: any) => {
                return `${p.data[0]}: ${p.data[1]}`;
            }
        },
        visualMap: {
            min: minVal,
            max: maxVal,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: '5%',
            inRange: {
                color: colors.length >= 2 ? colors : (colors.length === 1 ? ['#ebedf0', colors[0]] : ['#ebedf0', '#2ecc71'])
            },
            textStyle: {
                color: theme === 'dark' ? '#ccc' : '#333'
            }
        },
        calendar: {
            top: '15%',
            left: '10%',
            right: '10%',
            cellSize: ['auto', 20],
            range: range,
            itemStyle: {
                borderWidth: 0.5,
                borderColor: theme === 'dark' ? '#333' : '#eee'
            },
            yearLabel: { show: true },
            dayLabel: {
                firstDay: 1,
                nameMap: 'en',
                color: theme === 'dark' ? '#ccc' : '#333'
            },
            monthLabel: {
                nameMap: 'en',
                color: theme === 'dark' ? '#ccc' : '#333'
            },
            splitLine: {
                lineStyle: {
                    color: theme === 'dark' ? '#444' : '#ccc',
                    width: 1
                }
            }
        },
        series: {
            type: 'heatmap',
            coordinateSystem: 'calendar',
            data: chartData
        }
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
